import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Trophy, Calendar, Award, ExternalLink, Upload, Image as ImageIcon, FileType } from "lucide-react";
import api from "@/lib/api";

const blank = {
  title: "",
  achievementDate: "",
  issuer: "",
  description: "",
  certificateUrl: "",
  imageUrl: "",
  displayOrder: 1
};

export default function Achievements() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [certName, setCertName] = useState("No file selected");
  const [imageName, setImageName] = useState("No file selected");

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const res = await api.get("/Achievement");
      if (res.success) {
        setItems(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch achievements");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAchievements();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setCertName("No file selected");
    setImageName("No file selected");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      ...item,
      achievementDate: item.achievementDate ? item.achievementDate.substring(0, 7) : ""
    });
    setCertName(item.certificateUrl ? item.certificateUrl.split('/').pop() : "No file selected");
    setImageName(item.imageUrl ? item.imageUrl.split('/').pop() : "No file selected");
    setModalOpen(true);
  };

  const handleCertUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertName(file.name);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post('/Achievement/upload-certificate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success) {
        setForm({ ...form, certificateUrl: res.data });
        toast.success("Certificate uploaded");
      } else {
        toast.error(res.message || "Upload failed");
      }
    } catch (err) {
      toast.error(err.message || "Failed to upload certificate");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageName(file.name);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post('/Achievement/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success) {
        setForm({ ...form, imageUrl: res.data });
        toast.success("Image uploaded");
      } else {
        toast.error(res.message || "Upload failed");
      }
    } catch (err) {
      toast.error(err.message || "Failed to upload image");
    }
  };

  const save = async () => {
    if (!form.title) {
      toast.error("Title is required.");
      return;
    }

    const payload = {
      ...form,
      achievementDate: form.achievementDate ? `${form.achievementDate}-01` : null
    };

    try {
      if (editing) {
        const res = await api.put(`/Achievement/${editing.id}`, payload);
        if (res.success) {
          toast.success("Achievement updated.");
          fetchAchievements();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const res = await api.post("/Achievement", payload);
        if (res.success) {
          toast.success("Achievement added.");
          fetchAchievements();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/Achievement/${toDelete.id}`);
      if (res.success) {
        toast.success("Achievement removed.");
        fetchAchievements();
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setConfirmOpen(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    const [y, m] = d.split("-");
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    if (!m || !y) return d;
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  };

  return (
    <div>
      <PageHeader
        title="Achievements"
        description="Awards, recognitions and milestones — the highlights reel."
        testId="achievements"
        action={openCreate}
        actionLabel="Add Achievement"
        actionIcon={Plus}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((a) => (
          <Card
            key={a.id}
            data-testid={`achievement-card-${a.id}`}
            className="group p-0 overflow-hidden hover:border-foreground/20 transition-colors flex flex-col"
          >
            <div className="relative h-44 overflow-hidden bg-muted shrink-0 flex items-center justify-center">
              {a.imageUrl ? (
                <img
                  src={a.imageUrl}
                  alt={a.title}
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-muted-foreground/30 group-hover:scale-110 transition-transform duration-500">
                  <Trophy className="h-16 w-16" strokeWidth={1} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
              <Badge className="absolute top-3 left-3 bg-background/90 text-foreground border-0 backdrop-blur text-[10px] font-mono">
                <Trophy className="h-3 w-3 mr-1 text-primary" strokeWidth={1.75} />
                Achievement
              </Badge>
            </div>
            <div className="p-5 flex flex-col flex-1">
              <h3 className="font-display text-base font-semibold tracking-tight line-clamp-2 min-h-[2.75rem]">{a.title}</h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Award className="h-3 w-3" strokeWidth={1.5} />
                  {a.issuer}
                </span>
                <span className="inline-flex items-center gap-1 font-mono">
                  <Calendar className="h-3 w-3" strokeWidth={1.5} />
                  {formatDate(a.achievementDate)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-2">{a.description}</p>
              
              <div className="flex items-center gap-1 mt-auto pt-4 border-t border-border">
                {a.certificateUrl && (
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-xs h-7"
                    onClick={() => window.open(a.certificateUrl, '_blank')}
                  >
                    View certificate
                    <ExternalLink className="ml-1.5 h-3 w-3" />
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)} data-testid={`achievement-edit-${a.id}`}>
                    <Pencil className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive"
                    onClick={() => { setToDelete(a); setConfirmOpen(true); }}
                    data-testid={`achievement-delete-${a.id}`}
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="achievement-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit achievement" : "Add achievement"}</DialogTitle>
            <DialogDescription>Showcase an award, recognition or milestone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title</Label>
              <Input data-testid="achievement-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Achievement Date</Label>
                <Input type="month" data-testid="achievement-date" value={form.achievementDate} onChange={(e) => setForm({ ...form, achievementDate: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Issuer</Label>
                <Input data-testid="achievement-issuer" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea data-testid="achievement-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <UploadCard
                label="Certificate"
                description="Upload PDF or image"
                fileName={certName}
                testid="achievement-certificate"
                icon={FileType}
                onChange={handleCertUpload}
              />
              <UploadCard
                label="Cover Image"
                description="Upload image for display"
                fileName={imageName}
                testid="achievement-image"
                icon={ImageIcon}
                onChange={handleImageUpload}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="achievement-save">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete achievement?"
        description="This will remove the achievement from your portfolio."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}

const UploadCard = ({ label, description, fileName, testid, icon: Icon, onChange }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    <label className="mt-1.5 block cursor-pointer">
      <div className="flex items-center gap-3 border border-dashed border-border rounded-md px-3 py-3 hover:border-foreground/30 transition-colors">
        <span className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{fileName !== "No file selected" ? fileName : "Click to upload"}</p>
          <p className="text-[11px] font-mono text-muted-foreground truncate">{description}</p>
        </div>
        <Upload className="h-3.5 w-3.5 text-primary shrink-0" strokeWidth={1.75} />
      </div>
      <input type="file" className="hidden" data-testid={testid} onChange={onChange} />
    </label>
  </div>
);
